import React from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";

export default function Pagination({ pagination, onPageChange }) {
  const { currentPage, totalPages, pageSize, totalItems } = pagination;

  // Helper to generate page numbers with ellipsis logic
  const getPageList = () => {
    if (totalPages <= 5) {
      return [...Array(totalPages).keys()].map((i) => i + 1);
    }

    const pages = [1];

    if (currentPage <= 3) {
      pages.push(2, 3, 4, "…");
    } else if (currentPage >= totalPages - 2) {
      pages.push("…", totalPages - 3, totalPages - 2, totalPages - 1);
    } else {
      pages.push("…", currentPage - 1, currentPage, currentPage + 1, "…");
    }

    pages.push(totalPages);
    return pages;
  };

  const pageList = getPageList();

  return (
    <div className="mt-5 flex items-center justify-between overflow-x-auto rounded-lg bg-white px-2 py-3 dark:bg-navy-800 sm:px-4">
      {/* Mobile View: Prev & Next */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
        >
          Prev
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="relative ml-1 inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-navy-700 dark:text-white"
        >
          Next
        </button>
      </div>

      {/* Desktop View: Pagination Buttons + Text */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        {/* Showing x to y of z */}
        <p className="text-sm text-gray-700 dark:text-white">
          Showing{" "}
          <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span>{" "}
          to{" "}
          <span className="font-medium">
            {Math.min(currentPage * pageSize, totalItems)}
          </span>{" "}
          of <span className="font-medium">{totalItems}</span>
        </p>

        {/* Pagination Number Buttons */}
        <nav
          className="isolate inline-flex -space-x-px rounded-md shadow-sm"
          aria-label="Pagination"
        >
          {/* Previous Arrow */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center rounded-l-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 disabled:opacity-50 dark:bg-navy-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-navy-800"
          >
            <span className="sr-only">Previous</span>
            <MdChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>

          {/* Dynamic Page Buttons */}
          {pageList.map((page, idx) =>
            page === "…" ? (
              <span
                key={`ellipsis-${idx}`}
                className="relative inline-flex items-center px-3 py-2 text-xs text-gray-700 ring-1 ring-inset ring-gray-300 dark:text-gray-300 dark:ring-gray-600"
              >
                …
              </span>
            ) : (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`relative inline-flex items-center px-3 py-2 text-xs ${
                  page === currentPage
                    ? "z-10 bg-primary text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    : "text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 dark:bg-navy-700 dark:text-white dark:ring-gray-600"
                }`}
              >
                {page}
              </button>
            )
          )}

          {/* Next Arrow */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="relative inline-flex items-center rounded-r-md bg-white px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 disabled:opacity-50 dark:bg-navy-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-navy-800"
          >
            <span className="sr-only">Next</span>
            <MdChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </nav>
      </div>
    </div>
  );
}
